/**
 * xlsx.js — translate an Excel workbook without touching a single formula.
 *
 * WHY IT IS HERE, and the honest size of the prize. Demand for TRANSLATING a
 * spreadsheet is modest: «перевести эксель на русский» 1 151 impressions a
 * month, «перевод таблицы excel» 145. ⚠️ «перевести excel» shows 9 092 and does
 * NOT belong here — inside it sits «перевести В excel» (8 677), which is
 * conversion, not translation. The format is supported anyway for two reasons:
 * it came almost free (the engine is shared with pptx.js; only the «one cell is
 * one unit» rule and the formula guarantees are its own), and EVERY paid
 * competitor takes .xlsx — a refusal at the upload zone reads as "this tool
 * cannot", never as "this was expensive for them".
 *
 * WHY IT IS NOT A COPY OF docx.js. A spreadsheet stores its words in one
 * place — `xl/sharedStrings.xml` — and not as paragraphs. Excel already
 * de-duplicates: two cells reading "Итого" are ONE <si> entry that both cells
 * point at by index. So the unit here is the <si>, the indices must survive
 * untouched (they are what the cells refer to), and the translation memory
 * this tool applies elsewhere finds nothing left to save, which is correct
 * rather than a bug.
 *
 * WHAT IS DELIBERATELY NOT TOUCHED, and why each:
 *   · FORMULAS (<f>). They are not language. A translated SUM is a broken SUM.
 *   · SHEET NAMES. They are referenced by formulas as `Лист1!A1`; renaming the
 *     sheet without rewriting every reference breaks the workbook, and
 *     rewriting references means parsing a formula language to translate a tab
 *     label. The cost is wrong by orders of magnitude.
 *   · NUMBERS, DATES, NUMBER FORMATS. A cell holding 1 000,50 is a number in
 *     the file and its appearance comes from the locale of whoever opens it.
 *   · DEFINED NAMES, tables, pivot caches, conditional formatting rules.
 *
 * WHAT IS TOUCHED:
 *   · xl/sharedStrings.xml   — every cell that holds text;
 *   · xl/worksheets/sheetN.xml — inline strings (<is><t>), which some exporters
 *     write instead of using the shared table;
 *   · xl/charts/chartN.xml   — chart and axis titles (DrawingML <a:t>);
 *   · xl/comments*.xml       — cell comments.
 *
 * THE HONEST COMPROMISE, which the tool states rather than hides: a formula
 * that MATCHES text — VLOOKUP against a label, SUMIF("Москва"), a data
 * validation list — will stop matching once the label is translated and the
 * formula's quoted string is not. Translating the quoted strings inside
 * formulas would break the other half of the same workbooks. There is no
 * answer that is right for both, so the tool says which one it chose.
 *
 * Exposed as window.DTXlsx.
 */
(function (root) {
    'use strict';
    if (root.DTXlsx) return;

    var O = root.DTOoxml;

    var HAS_LETTER = /\p{L}/u;

    /* A shared string is `<si>…</si>`; inside it is either one `<t>` or a
       series of `<r>` runs each with their own `<t>`. Both shapes are one cell
       value, so both are one unit — a cell is never half-translated. */
    var SI_RE = /<si(?:\s[^>]*)?>[\s\S]*?<\/si>|<si(?:\s[^>]*)?\/>/g;
    var IS_RE = /<is(?:\s[^>]*)?>[\s\S]*?<\/is>/g;
    var T_RE = /(<t(?:\s[^>]*)?>)([\s\S]*?)(<\/t>)/g;

    /**
     * Split a part into raw markup and text-bearing blocks.
     *
     * A block owns EVERY `<t>` inside it: the text of the cell is their
     * concatenation, the translation goes into the first one and the rest are
     * emptied. Rich text inside one cell (half the string bold) therefore
     * flattens to the first run's formatting — the same compromise the other
     * adapters make, and reported the same way.
     */
    function splitBlocks(xml, blockRe) {
        var pieces = [];
        var last = 0, m;
        blockRe.lastIndex = 0;
        while ((m = blockRe.exec(xml)) !== null) {
            if (m.index > last) pieces.push({ raw: xml.slice(last, m.index) });
            pieces.push(parseBlock(m[0]));
            last = m.index + m[0].length;
        }
        if (last < xml.length) pieces.push({ raw: xml.slice(last) });
        return pieces;
    }

    /** One `<si>`/`<is>` → { chunks } where a chunk is raw XML or owned text. */
    function parseBlock(block) {
        var chunks = [];
        var last = 0, m;
        T_RE.lastIndex = 0;
        while ((m = T_RE.exec(block)) !== null) {
            if (m.index > last) chunks.push({ raw: block.slice(last, m.index) });
            chunks.push({ open: m[1], text: O.unescapeXml(m[2]) });
            last = m.index + m[0].length;
        }
        if (last < block.length) chunks.push({ raw: block.slice(last) });
        return { chunks: chunks };
    }

    function blockText(block) {
        return block.chunks.reduce(function (s, c) {
            return c.raw === undefined ? s + c.text : s;
        }, '');
    }

    function buildBlock(block) {
        return block.chunks.map(function (c) {
            if (c.raw !== undefined) return c.raw;
            // Excel drops leading and trailing spaces in a `<t>` unless told
            // otherwise, exactly like Word. A label that reads " Итого " must
            // not silently become "Итого" just because it passed through here.
            var open = c.open;
            if (/^\s|\s$/.test(c.text) && !/xml:space\s*=/.test(open)) {
                open = open.replace(/^<t/, '<t xml:space="preserve"');
            }
            return open + O.escapeXml(c.text) + '</t>';
        }).join('');
    }

    function isName(name) {
        return name === 'xl/sharedStrings.xml'
            || /^xl\/worksheets\/sheet\d+\.xml$/.test(name)
            || /^xl\/charts\/chart\d+\.xml$/.test(name)
            || /^xl\/(threadedComments\/)?comments\d*\.xml$/.test(name);
    }

    function orderedParts(zip) {
        var shared = [], sheets = [], charts = [], comments = [];
        Object.keys(zip.files).forEach(function (name) {
            if (!isName(name)) return;
            if (name === 'xl/sharedStrings.xml') shared.push(name);
            else if (/worksheets/.test(name)) sheets.push(name);
            else if (/charts/.test(name)) charts.push(name);
            else comments.push(name);
        });
        var num = function (s) { return parseInt((s.match(/(\d+)\.xml$/) || [0, 0])[1], 10); };
        sheets.sort(function (a, b) { return num(a) - num(b); });
        charts.sort(function (a, b) { return num(a) - num(b); });
        comments.sort();
        return shared.concat(sheets, charts, comments);
    }

    /**
     * open(file, JSZip) → { units, meta, build(translated) }
     */
    async function open(file, JSZip) {
        if (!O) throw new Error('ooxml_missing');
        var zip = await JSZip.loadAsync(file);
        if (!zip.file('xl/workbook.xml')) {
            throw new Error('not_a_xlsx');
        }

        var names = orderedParts(zip);
        var parts = {};      // name → { pieces, ns } for text parts
        var units = [];
        var sheets = 0;
        var flattened = 0;

        for (var i = 0; i < names.length; i++) {
            var name = names[i];
            var xml = await zip.file(name).async('string');

            if (/^xl\/charts\//.test(name)) {
                // Chart text is DrawingML, identical to a slide's — the shared
                // engine already knows it, so charts cost nothing extra.
                var r = O.unitsForPart(xml, name, O.A);
                parts[name] = { drawing: r.pieces };
                r.units.forEach(function (u) {
                    u.id = units.length;
                    u.drawing = true;
                    if (u.flattened) flattened++;
                    units.push(u);
                });
                continue;
            }

            if (/^xl\/worksheets\//.test(name)) sheets++;
            var re = name === 'xl/sharedStrings.xml' ? SI_RE : IS_RE;
            if (name !== 'xl/sharedStrings.xml' && !IS_RE.test(xml)) {
                // The overwhelmingly common sheet has no inline strings at all;
                // keeping its pieces would hold a multi-megabyte part in memory
                // for nothing.
                IS_RE.lastIndex = 0;
                continue;
            }
            IS_RE.lastIndex = 0;
            var pieces = splitBlocks(xml, re);
            parts[name] = { blocks: pieces };
            pieces.forEach(function (p, idx) {
                if (p.raw !== undefined) return;
                var text = blockText(p);
                if (!HAS_LETTER.test(text)) return;   // codes, numbers stored as text
                var owned = p.chunks.filter(function (c) { return c.raw === undefined; }).length;
                if (owned > 1) flattened++;
                units.push({ id: units.length, part: name, piece: idx, text: text, flattened: owned > 1 });
            });
        }

        return {
            kind: 'xlsx',
            units: units.map(function (u) { return { id: u.id, text: u.text }; }),
            meta: {
                parts: Object.keys(parts),
                paragraphs: units.length,
                pages: sheets,
                flattened: flattened,
                // Read by the tool to raise the one disclosure that belongs to
                // spreadsheets and to no other format.
                spreadsheet: true,
                chars: units.reduce(function (n, u) { return n + u.text.length; }, 0),
            },

            async build(translated) {
                units.forEach(function (u) {
                    var text = translated[u.id];
                    if (text == null || text === '') return;
                    var part = parts[u.part];
                    if (u.drawing) { O.placeTranslation(part.drawing, u, text); return; }
                    var block = part.blocks[u.piece];
                    var first = true;
                    block.chunks.forEach(function (c) {
                        if (c.raw !== undefined) return;
                        c.text = first ? String(text) : '';
                        first = false;
                    });
                });

                Object.keys(parts).forEach(function (nm) {
                    var p = parts[nm];
                    if (p.drawing) { zip.file(nm, O.buildPart(p.drawing, O.A)); return; }
                    zip.file(nm, p.blocks.map(function (x) {
                        return x.raw !== undefined ? x.raw : buildBlock(x);
                    }).join(''));
                });

                return zip.generateAsync({
                    type: 'blob',
                    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    compression: 'DEFLATE',
                });
            },
        };
    }

    root.DTXlsx = {
        open: open,
        // exposed for tests
        _splitBlocks: function (xml) { return splitBlocks(xml, SI_RE); },
        _blockText: blockText,
        _buildBlock: buildBlock,
        _orderedParts: orderedParts,
    };
})(typeof self !== 'undefined' ? self : this);
